import { useState } from "react";
import { motion } from "framer-motion";
import {
  KeyRound,
  Sparkles,
  ShieldCheck,
  Copy,
  ClipboardCheck,
  Rocket,
  ArrowRight,
  ExternalLink,
  Check,
} from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";

const AI_STUDIO_URL = "https://aistudio.google.com/app/apikey";
const stepIcons = [KeyRound, Sparkles, Copy, ClipboardCheck, Rocket];

export const ByokTutorial = () => {
  const { t } = useLang();
  const b = t.byok;
  const [active, setActive] = useState(0);
  const total = b.steps.length;

  return (
    <section
      id="byok"
      data-testid="byok-section"
      className="relative py-24 sm:py-32 overflow-hidden"
    >
      {/* Ambient glow */}
      <div className="pointer-events-none absolute -top-20 -left-24 h-80 w-80 rounded-full bg-emerald-500/15 blur-3xl" />
      <div className="pointer-events-none absolute bottom-0 right-0 h-80 w-80 rounded-full bg-violet-500/15 blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-[0.9fr_1.1fr] gap-14 lg:gap-20 items-start">
          {/* LEFT — intro / manifesto */}
          <div className="lg:sticky lg:top-28">
            <motion.span
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="text-xs font-bold uppercase tracking-[0.25em] text-emerald-400"
            >
              {b.overline}
            </motion.span>

            <div className="mt-5 overflow-hidden">
              <motion.h2
                initial={{ y: "110%" }}
                whileInView={{ y: "0%" }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                data-testid="byok-title"
                className="font-outfit text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-[1.05] tracking-tight text-white"
              >
                {b.title}
              </motion.h2>
            </div>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="mt-5 text-lg text-slate-400 leading-relaxed max-w-md"
            >
              {b.subtitle}
            </motion.p>

            {/* Badges */}
            <div className="mt-7 flex flex-wrap gap-3">
              <span
                data-testid="byok-badge-free"
                className="inline-flex items-center gap-2 rounded-full bg-emerald-400/15 px-4 py-1.5 text-sm font-bold text-emerald-300 ring-1 ring-emerald-400/25"
              >
                <Sparkles className="h-4 w-4" />
                {b.freeBadge}
              </span>
              <span
                data-testid="byok-badge-personal"
                className="inline-flex items-center gap-2 rounded-full bg-violet-400/15 px-4 py-1.5 text-sm font-bold text-violet-300 ring-1 ring-violet-400/25"
              >
                <ShieldCheck className="h-4 w-4" />
                {b.personalBadge}
              </span>
            </div>

            {/* Privacy note */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="mt-7 flex items-start gap-3 rounded-2xl border border-emerald-400/15 bg-emerald-500/[0.04] p-5"
            >
              <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-emerald-400" />
              <p className="text-sm text-slate-300 leading-relaxed">{b.note}</p>
            </motion.div>

            <a
              href={AI_STUDIO_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="byok-cta"
              className="group mt-8 inline-flex items-center gap-2 rounded-full bg-emerald-400 px-8 py-4 text-sm font-bold uppercase tracking-wider text-emerald-950 hover:bg-emerald-300 transition-colors duration-300 shadow-lg shadow-emerald-500/25"
            >
              {b.cta}
              <ExternalLink className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
          </div>

          {/* RIGHT — interactive stepper */}
          <div className="relative">
            {/* Progress rail */}
            <div className="absolute left-[27px] top-4 bottom-4 w-px bg-white/10" />
            <motion.div
              className="absolute left-[27px] top-4 w-px bg-gradient-to-b from-emerald-400 to-violet-400"
              initial={{ height: 0 }}
              animate={{ height: `${(active / (total - 1)) * 100}%` }}
              transition={{ duration: 0.5, ease: "easeInOut" }}
              style={{ maxHeight: "calc(100% - 2rem)" }}
            />

            <div className="space-y-3">
              {b.steps.map((s, i) => {
                const Icon = stepIcons[i] || KeyRound;
                const isActive = i === active;
                const isDone = i < active;
                return (
                  <motion.button
                    key={i}
                    type="button"
                    onClick={() => setActive(i)}
                    data-testid={`byok-step-${i}`}
                    initial={{ opacity: 0, x: 24 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: i * 0.08 }}
                    className={`relative w-full text-left flex items-start gap-5 rounded-2xl border p-5 pl-4 transition-all duration-300 ${
                      isActive
                        ? "border-emerald-400/40 bg-white/[0.06] shadow-lg shadow-emerald-500/10"
                        : "border-white/10 bg-white/[0.02] hover:bg-white/[0.04]"
                    }`}
                  >
                    {/* Number / icon node */}
                    <span
                      className={`relative z-10 grid place-items-center h-11 w-11 shrink-0 rounded-xl ring-1 transition-all duration-300 ${
                        isActive
                          ? "bg-emerald-400 text-emerald-950 ring-emerald-300"
                          : isDone
                          ? "bg-emerald-500/15 text-emerald-400 ring-emerald-400/30"
                          : "bg-[#0B0F1A] text-slate-400 ring-white/15"
                      }`}
                    >
                      {isDone ? (
                        <Check className="h-5 w-5" strokeWidth={2.75} />
                      ) : (
                        <Icon className="h-5 w-5" />
                      )}
                    </span>

                    <div className="min-w-0 flex-1">
                      <span
                        className={`text-[0.7rem] font-bold uppercase tracking-[0.2em] ${
                          isActive ? "text-emerald-400" : "text-slate-500"
                        }`}
                      >
                        {b.step} {String(i + 1).padStart(2, "0")}
                      </span>
                      <h3 className="mt-1 font-outfit text-lg font-bold text-white flex items-center gap-2">
                        {s.title}
                        {isActive && (
                          <ArrowRight className="h-4 w-4 text-emerald-400" />
                        )}
                      </h3>
                      <motion.div
                        initial={false}
                        animate={{
                          height: isActive ? "auto" : 0,
                          opacity: isActive ? 1 : 0,
                        }}
                        transition={{ duration: 0.35, ease: "easeInOut" }}
                        className="overflow-hidden"
                      >
                        <p className="mt-2 text-sm text-slate-400 leading-relaxed">
                          {s.desc}
                        </p>
                      </motion.div>
                    </div>
                  </motion.button>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
